// React imports
import React from 'react';

// Types
import Service from 'model/Service';
import AddOnService from '@spike/addon-service-model';
import { UseItemSelectionReturn } from './useItemSelection';
import { EnhancedProduct } from './useDiscountForm';

/**
 * useDrawerConfigs - Item Selection Drawer Configuration Hook
 *
 * Purpose:
 * Generates configuration objects for all ItemSelectionDrawer components,
 * enabling a data-driven rendering approach that eliminates code duplication.
 *
 * Responsibilities:
 * - Creates configuration for all 8 item selection drawers
 * - Maps data sources (items, selected items) to each drawer
 * - Connects selection handlers from useItemSelection hooks
 * - Provides drawer-specific labels and search placeholders
 * - Associates correct display configurations for each item type
 * - Memoizes configurations to prevent unnecessary re-renders
 *
 * Drawers Configured:
 * 1. Grooming Services Drawer
 * 2. Daycare Services Drawer
 * 3. Boarding Services Drawer
 * 4. Dental Services Drawer: ELIMINATED, goes with grooming
 * 5. Transportation Services Drawer
 * 6. Evaluation Services Drawer
 * 7. Add-ons Drawer
 * 8. Products Drawer
 *
 * Key Features:
 * - Single source of truth for all drawer configurations
 * - Configuration-driven rendering (map over configs vs 8 duplicate components)
 * - Type-safe configuration structure
 * - Performance optimized with useMemo and comprehensive dependency tracking
 * - Standardized drawer structure across all item types
 *
 * Configuration Structure:
 * Each config includes:
 * - id: Unique identifier for the drawer (used as React key)
 * - isOpen: Current open/closed state from useItemSelection
 * - title: Drawer header title
 * - items: All available items for selection
 * - selectedItems: Currently selected items
 * - selection: Complete useItemSelection handler object
 * - selectAllLabel: Label for "Select All" checkbox
 * - searchPlaceholder: Placeholder text for search input
 * - itemDisplay: Display configuration object (how to render items)
 *
 * When to use:
 * Use this hook in the AddDiscount component to get all drawer configurations
 * at once, then map over them to render ItemSelectionDrawer components.
 * This eliminates ~140 lines of repetitive JSX code.
 *
 * @param {UseDrawerConfigsParams} params - All drawer data, selections, and handlers
 * @returns {DrawerConfig[]} Array of configuration objects for all item selection drawers
 */

interface DrawerConfig {
    id: string;
    isOpen: boolean;
    title: string;
    items: any[];
    selectedItems: any[];
    selection: UseItemSelectionReturn<any>;
    selectAllLabel: string;
    searchPlaceholder: string;
    itemDisplay: {
        getTitle: (item: any) => string;
        getSubtitle: (item: any) => string;
        getPrice: (item: any) => string;
        getTitleFull?: (item: any) => string;
        getSubtitleFull?: (item: any) => string;
    };
}

interface UseDrawerConfigsParams {
    groomingSelection: UseItemSelectionReturn<Service>;
    groomingServices: Service[];
    selectedGroomingServices: Service[];
    daycareSelection: UseItemSelectionReturn<Service>;
    daycareServices: Service[];
    selectedDaycareServices: Service[];
    boardingSelection: UseItemSelectionReturn<Service>;
    boardingServices: Service[];
    selectedBoardingServices: Service[];
    transportationSelection: UseItemSelectionReturn<Service>;
    transportationServices: Service[];
    selectedTransportationServices: Service[];
    evaluationSelection: UseItemSelectionReturn<Service>;
    evaluationServices: Service[];
    selectedEvaluationServices: Service[];
    addonsSelection: UseItemSelectionReturn<AddOnService>;
    addons: AddOnService[];
    selectedAddons: AddOnService[];
    productsSelection: UseItemSelectionReturn<EnhancedProduct>;
    products: EnhancedProduct[];
    selectedProducts: EnhancedProduct[];
    serviceDisplay: {
        getTitle: (item: Service) => string;
        getSubtitle: (item: Service) => string;
        getPrice: (item: Service) => string;
    };
    addonDisplay: {
        getTitle: (item: AddOnService) => string;
        getSubtitle: (item: AddOnService) => string;
        getPrice: (item: AddOnService) => string;
    };
    productDisplay: {
        getTitle: (item: EnhancedProduct) => string;
        getSubtitle: (item: EnhancedProduct) => string;
        getPrice: (item: EnhancedProduct) => string;
        getTitleFull?: (item: EnhancedProduct) => string;
        getSubtitleFull?: (item: EnhancedProduct) => string;
    };
}

/**
 * Custom hook to provide drawer configurations for all item selection drawers
 * Encapsulates the configuration logic for rendering multiple ItemSelectionDrawer components
 */
export const useDrawerConfigs = ({
    groomingSelection,
    groomingServices,
    selectedGroomingServices,
    daycareSelection,
    daycareServices,
    selectedDaycareServices,
    boardingSelection,
    boardingServices,
    selectedBoardingServices,
    transportationSelection,
    transportationServices,
    selectedTransportationServices,
    evaluationSelection,
    evaluationServices,
    selectedEvaluationServices,
    addonsSelection,
    addons,
    selectedAddons,
    productsSelection,
    products,
    selectedProducts,
    serviceDisplay,
    addonDisplay,
    productDisplay
}: UseDrawerConfigsParams): DrawerConfig[] => {
    return React.useMemo(
        () => [
            {
                id: 'grooming',
                isOpen: groomingSelection.isOpen,
                title: 'Select Grooming Services',
                items: groomingServices,
                selectedItems: selectedGroomingServices,
                selection: groomingSelection,
                selectAllLabel: 'All Services',
                searchPlaceholder: 'Search by service name',
                itemDisplay: serviceDisplay
            },
            {
                id: 'daycare',
                isOpen: daycareSelection.isOpen,
                title: 'Select Daycare Services',
                items: daycareServices,
                selectedItems: selectedDaycareServices,
                selection: daycareSelection,
                selectAllLabel: 'All Services',
                searchPlaceholder: 'Search by service name',
                itemDisplay: serviceDisplay
            },
            {
                id: 'boarding',
                isOpen: boardingSelection.isOpen,
                title: 'Select Boarding Services',
                items: boardingServices,
                selectedItems: selectedBoardingServices,
                selection: boardingSelection,
                selectAllLabel: 'All Services',
                searchPlaceholder: 'Search by service name',
                itemDisplay: serviceDisplay
            },
            {
                id: 'transportation',
                isOpen: transportationSelection.isOpen,
                title: 'Select Transportation Services',
                items: transportationServices,
                selectedItems: selectedTransportationServices,
                selection: transportationSelection,
                selectAllLabel: 'All Services',
                searchPlaceholder: 'Search by service name',
                itemDisplay: serviceDisplay
            },
            {
                id: 'evaluation',
                isOpen: evaluationSelection.isOpen,
                title: 'Select Evaluation Services',
                items: evaluationServices,
                selectedItems: selectedEvaluationServices,
                selection: evaluationSelection,
                selectAllLabel: 'All Services',
                searchPlaceholder: 'Search by service name',
                itemDisplay: serviceDisplay
            },
            {
                id: 'addons',
                isOpen: addonsSelection.isOpen,
                title: 'Select Add-ons',
                items: addons,
                selectedItems: selectedAddons,
                selection: addonsSelection,
                selectAllLabel: 'All Add-ons',
                searchPlaceholder: 'Search by add-on name',
                itemDisplay: addonDisplay
            },
            {
                id: 'products',
                isOpen: productsSelection.isOpen,
                title: 'Select Products',
                items: products,
                selectedItems: selectedProducts,
                selection: productsSelection,
                selectAllLabel: 'All Products',
                searchPlaceholder: 'Search by product name',
                itemDisplay: productDisplay
            }
        ],
        [
            groomingSelection,
            groomingServices,
            selectedGroomingServices,
            daycareSelection,
            daycareServices,
            selectedDaycareServices,
            boardingSelection,
            boardingServices,
            selectedBoardingServices,
            transportationSelection,
            transportationServices,
            selectedTransportationServices,
            evaluationSelection,
            evaluationServices,
            selectedEvaluationServices,
            addonsSelection,
            addons,
            selectedAddons,
            productsSelection,
            products,
            selectedProducts,
            serviceDisplay,
            addonDisplay,
            productDisplay
        ]
    );
};
